import { useEffect, useRef, useState } from "react";
import { DoorOpen, DoorClosed, Car, LogIn, LogOut, Play, Pause, Camera, X, Lock, ClipboardList } from "lucide-react";
import { track } from "@/lib/track";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PLATE_LETTERS = "ABCDEFGHJKLMNPRSTUVWXYZ";
const LS_KEY = "mp_sim_v3";
const SSG_BANNER = "https://customer-assets-gfyr7b9c.emergentagent.net/job_checkpoint-monitor/artifacts/p2bxbg7t_image.png";

function randomPlate() {
  const l = () => PLATE_LETTERS[Math.floor(Math.random() * PLATE_LETTERS.length)];
  return `WI-${l()}${l()} ${Math.floor(1000 + Math.random() * 8999)}`;
}

function timeStr(ms) {
  return new Date(ms).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
}

function fmtSeit(ms) {
  const t = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(t / 3600), m = Math.floor((t % 3600) / 60), s = t % 60;
  const pad = (n) => String(n).padStart(2, "0");
  return h > 0 ? `seit: ${pad(h)} Stunden ${pad(m)} Minuten ${pad(s)} Sekunden` : `seit: ${pad(m)} Minuten ${pad(s)} Sekunden`;
}

function fmtDur(ms) {
  const t = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(t / 3600), m = Math.floor((t % 3600) / 60), s = t % 60;
  const pad = (n) => String(n).padStart(2, "0");
  return h > 0 ? `${pad(h)}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
}

function clockHM(ms) {
  return new Date(ms).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
}

function fmtHM(ms) {
  const t = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(t / 3600), m = Math.floor((t % 3600) / 60);
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(h)}:${pad(m)}`;
}

function loadState() {
  try {
    const raw = JSON.parse(localStorage.getItem(LS_KEY));
    if (raw && raw.status) return raw;
  } catch (e) { /* ignore */ }
  const now = Date.now();
  return {
    status: "geschlossen",
    lastChangeMs: now,
    lastVehicle: { plate: randomPlate(), timeMs: now },
    inside: [],
    events: [{ id: 1, type: "close", tsMs: now, text: "Sperre geschlossen" }],
    sim: true,
    _eid: 1,
  };
}

export default function MarktplatzWidget({ ssg = false }) {
  const [st, setSt] = useState(loadState);
  const [now, setNow] = useState(Date.now());
  const [scan, setScan] = useState(false);
  const [scanStep, setScanStep] = useState("camera");
  const [scannedPlate, setScannedPlate] = useState("");
  const [scannedReal, setScannedReal] = useState(false);
  const [showList, setShowList] = useState(false);
  const [camError, setCamError] = useState(false);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const stRef = useRef(st);
  stRef.current = st;

  useEffect(() => { localStorage.setItem(LS_KEY, JSON.stringify(st)); }, [st]);
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const push = (s, type, text, extra = {}) => {
    const id = (s._eid || 0) + 1;
    return { ...s, _eid: id, events: [{ id, type, tsMs: Date.now(), text, ...extra }, ...s.events].slice(0, 60) };
  };

  const openBarrier = () => setSt((s) => (s.status === "offen" ? s : push({ ...s, status: "offen", lastChangeMs: Date.now() }, "open", "Sperre geöffnet")));
  const closeBarrier = () => setSt((s) => (s.status === "geschlossen" ? s : push({ ...s, status: "geschlossen", lastChangeMs: Date.now() }, "close", "Sperre geschlossen")));

  const einfahrt = (plate, real = false) => setSt((s) => {
    const p = plate || randomPlate();
    const now2 = Date.now();
    let ns = { ...s, lastVehicle: { plate: p, timeMs: now2 }, inside: [...s.inside, { plate: p, inMs: now2 }] };
    ns = push(ns, "in", `Einfahrt ${p}`, { plate: p, in_ms: now2, real });
    return ns;
  });

  const ausfahrt = (otherGate = false) => setSt((s) => {
    const inside = [...s.inside];
    const v = inside.shift() || { plate: s.lastVehicle.plate, inMs: s.lastVehicle.timeMs };
    const text = otherGate ? `Ausfahrt an Sperre 3 ${v.plate}` : `Ausfahrt ${v.plate}`;
    return push({ ...s, inside }, "out", text, { plate: v.plate, in_ms: v.inMs, real: false });
  });

  const ausfahrtByPlate = (plate, real = false) => setSt((s) => {
    const inside = [...s.inside];
    const idx = inside.findIndex((v) => v.plate === plate);
    let in_ms = null;
    if (idx >= 0) { in_ms = inside[idx].inMs; inside.splice(idx, 1); }
    return push({ ...s, inside }, "out", `Ausfahrt ${plate}`, { plate, in_ms, real });
  });

  // Kamera nur im Schritt "camera" aktiv
  useEffect(() => {
    if (!scan || scanStep !== "camera") {
      if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
      return;
    }
    setCamError(false);
    let cancelled = false;
    navigator.mediaDevices?.getUserMedia({ video: { facingMode: "environment" } })
      .then((stream) => {
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) videoRef.current.srcObject = stream;
      })
      .catch(() => setCamError(true));
    return () => { cancelled = true; };
  }, [scan, scanStep]);

  const openScan = () => { track("scan_open", { mode: "simulation" }); setScannedPlate(""); setScannedReal(false); setScanStep("camera"); setScan(true); };
  const closeScan = () => { setScan(false); setScanStep("camera"); };

  const confirmScan = async () => {
    let b64 = null;
    const v = videoRef.current;
    if (v && v.videoWidth) {
      const c = document.createElement("canvas");
      c.width = v.videoWidth;
      c.height = v.videoHeight;
      c.getContext("2d").drawImage(v, 0, 0);
      b64 = c.toDataURL("image/jpeg", 0.8);
    }
    setScanStep("scanning"); // Kamera wird dadurch ausgeschaltet
    let plate = null, real = false;
    if (b64) {
      try {
        const r = await fetch(`${API}/scan-plate`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ image_base64: b64 }),
        });
        const j = await r.json();
        if (j.plate && !/UNBEKANNT/i.test(j.plate) && /\d/.test(j.plate)) { plate = j.plate.trim(); real = true; }
      } catch (e) { /* ignore */ }
    }
    if (!plate) { plate = randomPlate(); real = false; }
    track("plate_recognized", { mode: "simulation", real });
    setScannedPlate(plate);
    setScannedReal(real);
    setScanStep("result");
  };

  const toggleSim = () => setSt((s) => ({ ...s, sim: !s.sim }));

  useEffect(() => {
    if (!st.sim) return;
    const t = setInterval(() => {
      const s = stRef.current;
      const r = Math.random();
      if (s.status === "geschlossen") {
        if (r < 0.7) { einfahrt(randomPlate()); setTimeout(openBarrier, 700); }
      } else {
        if (r < 0.4) { ausfahrt(false); setTimeout(closeBarrier, 700); }
        else if (r < 0.6) ausfahrt(true);
        else if (r < 0.8) einfahrt(randomPlate());
        else closeBarrier();
      }
    }, 3800);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [st.sim]);

  const offen = st.status === "offen";
  const elapsed = now - st.lastChangeMs;
  const fillPct = offen ? Math.min(100, (elapsed / 180000) * 100) : 0;

  const iconFor = (type) =>
    type === "open" ? <DoorOpen size={15} /> :
    type === "close" ? <DoorClosed size={15} /> :
    type === "in" ? <LogIn size={15} /> : <LogOut size={15} />;

  return (
    <div className={`mp-widget mp-live ${st.status}`} data-testid="marktplatz-widget">
      {ssg && (
        <div className="mp-banner"><img src={SSG_BANNER} alt="SSG" /></div>
      )}
      <div className="mp-logo-strip">
        <a href="/" className="mp-brand" data-testid="brand-home" aria-label="Zur Startseite">
          <span className="mp-brand-bar" />
          <div className="mp-brand-title">SPERREN<span className="dot">.</span>DIGITAL</div>
          <div className="mp-brand-sub">IoT für Zufahrtssperren</div>
        </a>
        <span className="mp-simtag" data-testid="sim-tag">Simulation</span>
      </div>

      <div className="mp-live-head">
        {offen && <div className="mp-fill" style={{ height: `${fillPct}%` }} data-testid="open-fill" />}
        <div className="mp-barrier tall" data-testid="barrier-icon">
          <span className="base" />
          <span className="post" />
          <span className="beam" />
          <span className="arm" />
        </div>
        <div className="mp-live-main">
          <div className="mp-toprow">
            <div>
              <div className="mp-loc">Marktplatz</div>
              <div className="mp-sub">Letzte Änderung: {timeStr(st.lastChangeMs)}</div>
            </div>
          </div>
          <div className="mp-wordline">
            <span className="word" data-testid="status-word">{offen ? "OFFEN" : "GESCHLOSSEN"}</span>
            {offen ? (
              <span className="timer" data-testid="open-timer">{fmtSeit(elapsed)}</span>
            ) : (
              <span className="mp-secured"><Lock size={18} /> gesichert</span>
            )}
          </div>
        </div>
      </div>

      <div className="mp-body">
        <div className="mp-vehicle" data-testid="last-vehicle">
          <Car size={22} color="#0d6efd" />
          <div>
            <div className="cap">Letztes Fahrzeug</div>
            <div style={{ marginTop: 2 }}><span className="al-plate">{st.lastVehicle.plate}</span></div>
          </div>
          <div className="time">
            <div className="cap">Einfahrt</div>
            <div style={{ marginTop: 2, fontWeight: 600, color: "#212529" }}>{timeStr(st.lastVehicle.timeMs)}</div>
          </div>
        </div>

        <ul className="mp-log" data-testid="event-log">
          {st.events.map((ev) => {
            const cls = ev.type === "open" || ev.type === "close" ? ev.type : (ev.real ? "real" : "sim");
            return (
              <li key={ev.id}>
                <span className="lt">{timeStr(ev.tsMs)}</span>
                <span className={`li-ico ${cls}`}>{iconFor(ev.type)}</span>
                <span>
                  {ev.text}
                  {ev.type === "out" && ev.in_ms != null && (
                    <span className="mp-opendur"> ({clockHM(ev.in_ms)} - {clockHM(ev.tsMs)})</span>
                  )}
                </span>
              </li>
            );
          })}
        </ul>

        <div className="mp-controls">
          <button className="mp-btn primary" onClick={openScan} data-testid="btn-vehicle">
            <Camera size={15} /> Fahrzeug erfassen
          </button>
          <button className="mp-btn ghost" onClick={() => setShowList(true)} data-testid="btn-list">
            <ClipboardList size={15} /> Fahrzeugliste
          </button>
          <button className={`mp-btn ghost ${st.sim ? "on" : ""}`} onClick={toggleSim} data-testid="btn-sim">
            {st.sim ? <Pause size={15} /> : <Play size={15} />} Live-Simulation
          </button>
        </div>
      </div>

      {scan && (
        <div className="mp-scan-overlay" data-testid="scan-modal" onClick={closeScan}>
          <div className="mp-scan-box" onClick={(e) => e.stopPropagation()}>
            <div className="mp-scan-head">
              <span>
                {scanStep === "result" ? "Kennzeichen erkannt" : scanStep === "scanning" ? "Kennzeichenerkennung" : "Nummernschild Scannen"}
              </span>
              <button className="mp-scan-x" onClick={closeScan} data-testid="scan-close" aria-label="Schließen"><X size={18} /></button>
            </div>

            {scanStep === "camera" && (
              <>
                <div className="mp-scan-cam">
                  {!camError ? (
                    <video ref={videoRef} autoPlay playsInline muted className="mp-scan-video" />
                  ) : (
                    <div className="mp-scan-fallback"><Camera size={40} /><span>Kamera nicht verfügbar</span></div>
                  )}
                  <div className="mp-scan-frame"><span className="mp-scan-line" /></div>
                </div>
                <button className="mp-btn primary mp-scan-confirm" onClick={confirmScan} data-testid="scan-confirm">
                  <Camera size={15} /> Nummernschild erfassen
                </button>
              </>
            )}

            {scanStep === "scanning" && (
              <div className="mp-scan-progress" data-testid="scan-progress">
                <Camera size={40} className="mp-spin" />
                <span>Kennzeichenerkennung läuft…</span>
              </div>
            )}

            {scanStep === "result" && (
              <div className="mp-scan-result" data-testid="scan-result">
                <span className={`al-plate mp-plate-big ${scannedReal ? "" : "sim"}`}>{scannedPlate}</span>
                <div className="mp-scan-srclabel">{scannedReal ? "Kamera erkannt" : "Simuliert"}</div>
                <div className="mp-scan-mode">
                  <button className="mp-btn primary" onClick={() => { track("einfahrt", { mode: "simulation", real: scannedReal }); einfahrt(scannedPlate, scannedReal); closeScan(); }} data-testid="result-einfahrt">
                    <LogIn size={16} /> Einfahrt
                  </button>
                  <button className="mp-btn toggle" onClick={() => { track("ausfahrt", { mode: "simulation", real: scannedReal }); ausfahrtByPlate(scannedPlate, scannedReal); closeScan(); }} data-testid="result-ausfahrt">
                    <LogOut size={16} /> Ausfahrt
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {showList && (
        <div className="mp-scan-overlay" data-testid="list-modal" onClick={() => setShowList(false)}>
          <div className="mp-scan-box" onClick={(e) => e.stopPropagation()}>
            <div className="mp-scan-head">
              <span>Fahrzeugliste</span>
              <button className="mp-scan-x" onClick={() => setShowList(false)} data-testid="list-close" aria-label="Schließen"><X size={18} /></button>
            </div>
            <div className="mp-list">
              <div className="mp-list-cat">Im Gelände ({st.inside.length})</div>
              {st.inside.length === 0 && <div className="mp-list-empty">Keine Fahrzeuge im Gelände</div>}
              {[...st.inside].reverse().map((v, i) => (
                <div className="mp-list-row inside" key={`in-${i}`}>
                  <span className="al-plate">{v.plate}</span>
                  <span className="mp-list-time">{fmtHM(now - v.inMs)} <small>im Gelände</small></span>
                </div>
              ))}
              <div className="mp-list-cat">Ausgefahren</div>
              {st.events.filter((e) => e.type === "out").length === 0 && <div className="mp-list-empty">Noch keine Ausfahrten</div>}
              {st.events.filter((e) => e.type === "out").map((e) => (
                <div className="mp-list-row" key={`out-${e.id}`}>
                  <span className="al-plate">{e.plate}</span>
                  <span className="mp-list-time">{e.in_ms != null ? `${clockHM(e.in_ms)} - ${clockHM(e.tsMs)}` : clockHM(e.tsMs)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
